// Cotas del control del enganche (paso 06). El plan NO alcanza: una política especial de la tienda
// sube el mínimo exigible y la barra tiene que dejar de ofrecer un monto que el motor va a rechazar.
import { ApiError } from '@/api/errors';
import type { EffectiveTerms, QuotePlan } from './types';

/** Cotas efectivas en decimal sobre 1 (`0.4` = 40 %). `null` = la pantalla no las conoce. */
export interface DownPaymentPcts {
    minPct: number | null;
    maxPct: number | null;
}

/** Arranque: sin cotizar, la pantalla solo tiene las del plan. */
export const UNKNOWN_DOWN_PAYMENT_PCTS: DownPaymentPcts = { minPct: null, maxPct: null };

/** Margen que el mock deja libre sobre el precio para que el crédito financie algo. */
const MIN_FINANCED_PESOS = 100;

/** Tolerancia al error de coma flotante de `precio × pct` antes de redondear a centavos. */
const CENTS_EPSILON = 1e-6;

/** Piso en centavos, expresado en pesos: el mínimo nunca sube de un centavo válido. */
function ceilToCentPesos(cents: number): number {
    return Math.ceil(cents - CENTS_EPSILON) / 100;
}

/** Techo en centavos, expresado en pesos: el máximo nunca baja de un centavo válido. */
function floorToCentPesos(cents: number): number {
    return Math.floor(cents + CENTS_EPSILON) / 100;
}

/** Un pct del motor es una fracción sobre 1: fuera de (0, 1] no es una cota, es un dato roto. */
function usablePct(value: unknown): number | null {
    if (typeof value !== 'number' || !Number.isFinite(value)) return null;
    return value > 0 && value <= 1 ? value : null;
}

/** Lee un pct del `data` de un error tipado del motor. */
function pctFromErrorData(
    error: ApiError,
    key: 'minDownPaymentPct' | 'maxDownPaymentPct',
): number | null {
    const data = error.data as Record<string, unknown> | null | undefined;
    return usablePct(data?.[key]);
}

/**
 * Piso de enganche que impone el techo de monto financiable: lo que el plan no financia lo tiene
 * que poner el cliente. `null` si el techo no acota (financia el equipo entero o más).
 */
export function floorPctFromFinancingCap(
    priceCents: number,
    maxFinancedCents: number | null | undefined,
): number | null {
    if (typeof maxFinancedCents !== 'number' || !Number.isFinite(maxFinancedCents)) return null;
    if (priceCents <= 0 || maxFinancedCents >= priceCents) return null;
    return Math.max(0, (priceCents - maxFinancedCents) / priceCents);
}

/** El más exigente de dos pisos. Los dos tienen que cumplirse, así que manda el mayor. */
function strictestFloor(a: number | null, b: number | null): number | null {
    if (a === null) return b;
    if (b === null) return a;
    return Math.max(a, b);
}

/**
 * Cotas que el motor reveló al rechazar. Un rechazo sin su cifra conserva lo aprendido: la pantalla
 * nunca desaprende un mínimo que ya conoce.
 *
 * `AMOUNT_EXCEEDS_LIMIT` no habla del enganche, pero lo acota: lo que el plan no financia sale del
 * bolsillo del cliente. Sin esto la barra ofrece montos que el motor ya rechazó.
 */
export function learnDownPaymentPcts(
    previous: DownPaymentPcts,
    error: unknown,
    priceCents: number,
): DownPaymentPcts {
    if (!(error instanceof ApiError)) return previous;
    if (error.code === 'DOWN_PAYMENT_TOO_LOW') {
        const minPct = pctFromErrorData(error, 'minDownPaymentPct');
        return minPct === null ? previous : { ...previous, minPct };
    }
    if (error.code === 'DOWN_PAYMENT_TOO_HIGH') {
        const maxPct = pctFromErrorData(error, 'maxDownPaymentPct');
        return maxPct === null ? previous : { ...previous, maxPct };
    }
    if (error.code === 'AMOUNT_EXCEEDS_LIMIT') {
        const data = error.data as Record<string, unknown> | null | undefined;
        const cap = data?.maxFinancedAmountCents;
        const minPct = strictestFloor(
            previous.minPct,
            floorPctFromFinancingCap(priceCents, typeof cap === 'number' ? cap : null),
        );
        // El techo de enganche viaja con este rechazo aunque no lo cause: es lo que permite ver la
        // contradicción al primer intento, sin que el vendedor tenga que toparse con un 2º rechazo.
        const maxPct = pctFromErrorData(error, 'maxDownPaymentPct') ?? previous.maxPct;
        if (minPct === previous.minPct && maxPct === previous.maxPct) return previous;
        return { ...previous, minPct, maxPct };
    }
    return previous;
}

/**
 * Cotas que la cotización exitosa declara. Manda sobre lo aprendido de un rechazo: es más fresca.
 *
 * El techo de monto financiable también acota el enganche por abajo, así que el piso es el más
 * exigente de los dos.
 */
export function pctsFromEffectiveTerms(
    terms: EffectiveTerms | undefined,
    priceCents: number,
): DownPaymentPcts | null {
    if (terms === undefined) return null;
    return {
        minPct: strictestFloor(
            usablePct(terms.minDownPaymentPct),
            floorPctFromFinancingCap(priceCents, terms.maxFinancedAmountCents),
        ),
        maxPct: usablePct(terms.maxDownPaymentPct),
    };
}

/**
 * Rango en pesos (con centavos) del control del enganche. La cota efectiva MANDA sobre la del plan: ya trae
 * aplicadas la promoción y las políticas, así que puede apretar el mínimo o aflojarlo.
 */
export function downPaymentRange(
    priceCents: number,
    plan: QuotePlan,
    effective: DownPaymentPcts,
): { min: number; max: number } {
    const minPct = effective.minPct ?? plan.minDownPaymentPct;
    const min = ceilToCentPesos(priceCents * minPct);

    const maxPct = effective.maxPct ?? plan.maxDownPaymentPct ?? Number.POSITIVE_INFINITY;
    const maxFromPct = Number.isFinite(maxPct)
        ? floorToCentPesos(priceCents * maxPct)
        : Number.MAX_SAFE_INTEGER;

    // Techo práctico del mock; y el máximo nunca queda por debajo del mínimo (rango degenerado).
    const max = Math.max(min, Math.min(maxFromPct, priceCents / 100 - MIN_FINANCED_PESOS));
    return { min, max };
}

/**
 * ¿El plan puede con este equipo? `false` cuando el enganche que haría falta para respetar el techo
 * de monto pasa del enganche máximo: ahí no existe NINGÚN monto válido y pedir que se ajuste la
 * barra es mandar al vendedor a un callejón sin salida.
 *
 * `true` cuando no se conoce alguna de las dos cotas: no saber no es una contradicción.
 */
export function noFeasibleDownPayment(pcts: DownPaymentPcts): boolean {
    if (pcts.minPct === null || pcts.maxPct === null) return false;
    return pcts.minPct > pcts.maxPct;
}

/**
 * Rango del editor de rescate: sin plan solo se conoce lo que reveló el rechazo. `null` en un
 * extremo significa que no hay cota que anunciar y el monto lo valida la cotización.
 */
export function recoveryDownPaymentRange(
    priceCents: number,
    effective: DownPaymentPcts,
): { min: number | null; max: number | null } {
    return {
        min: effective.minPct === null ? null : ceilToCentPesos(priceCents * effective.minPct),
        max: effective.maxPct === null ? null : floorToCentPesos(priceCents * effective.maxPct),
    };
}

/** Enganche mínimo en centavos, igual que el silo: el mayor entre el % del plan y lo que el tope no financia. */
export function minDownPaymentCents(
    priceCents: number,
    minPct: number,
    maxFinancedCents: number | null | undefined,
): number {
    const policyFloor = Math.ceil(priceCents * minPct - CENTS_EPSILON);
    const capFloor = typeof maxFinancedCents === 'number' ? priceCents - maxFinancedCents : 0;
    return Math.max(policyFloor, capFloor);
}
